import React, { useState, useEffect, useRef } from "react";
import { Constants, CommonConstants } from "../../../constants/appConstants";
import { Ordersnew } from "../../../models/csos/ordersNew";
import { OrdersUpdate } from "../../../models/csos/ordersUpdate";
import { SummaryRequest } from "../../../models/csos/summaryRequest";
import { SummaryResponse } from "../../../models/csos/summaryResponse";
import { csosApiService } from "../../../services/api/csosApiService";
import { userService } from "../../../services/userService";
import { useOrderDetailsService } from "../../../services/component/orderDetailsService";
import { formatCurrency } from "../../../utils/formatCurrency";
import ModalContentCenter from "../../modalContentCenter/ModalContentCenter";
import "./SignApproveOrderLayer.css";

interface SignApproveOrderLayerProps {
  onSignEmit: () => void;
}

const SignApproveOrderLayer: React.FC<SignApproveOrderLayerProps> = ({ onSignEmit }) => {
  const orderDetailsService = useOrderDetailsService();
  const [flagCheckHeader, setFlagCheckHeader] = useState(true);
  const [enableButton, setEnableButton] = useState(false);
  const [totalOrderSentForSigning, setTotalOrderSentForSigning] = useState(0);
  const [actualOrderSentForSigning, setActualOrderSentForSigning] = useState(0);
  const [loggedInUser] = useState(userService.getCurrentUser());
  const [failureMessage, setFailureMessage] = useState("");
  const [showSignApproveOrderModal, setShowSignApproveOrderModal] = useState(false);
  const [showSignOrdersPopUp, setShowSignOrdersPopUp] = useState(false);
  const [showOverRiddenPopUp, setShowOverRiddenPopUp] = useState(false);
  const [showFailedMessagePopUp, setShowFailedMessagePopUp] = useState(false);
  const [approvedOrders, setApprovedOrders] = useState<SummaryResponse>(
    new SummaryResponse("0", "0", 0, [])
  );
  const passwordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsub = orderDetailsService.openSignApprove$.subscribe((openSignLayer) => {
      if (openSignLayer) {
        openApprovedOrders();
      }
    });
    return () => unsub();
    // eslint-disable-next-line
  }, [orderDetailsService]);

  const openApprovedOrders = () => {
    const summaryRequest = new SummaryRequest();
    summaryRequest.sort = "";
    summaryRequest.status = CommonConstants.APPROVED;
    setFlagCheckHeader(true);
    csosApiService.fetchtOrdersData(summaryRequest).then((res) => {
      if (res.body) {
        const orders = res.body;
        orders.orders.forEach((o) => { o.checkedValue = true; });
        setApprovedOrders(orders);
        setShowSignApproveOrderModal(true);
        enableSignButton(orders.orders);
      }
    }).catch(() => {
      setFailureMessage(CommonConstants.SIGNED_NOT_SIGNED);
      setShowFailedMessagePopUp(true);
    });
  };

  const enableSignButton = (orders: any[]) => {
    let enabled = false;
    for (const o of orders) {
      if (o.checkedValue) { enabled = true; break; }
    }
    setEnableButton(enabled);
  };

  const selectHeader = () => {
    const newVal = !flagCheckHeader;
    setFlagCheckHeader(newVal);
    const updated = { ...approvedOrders };
    updated.orders.forEach((o) => { o.checkedValue = newVal; });
    setApprovedOrders(updated);
    enableSignButton(updated.orders);
  };

  const updateSelectAll = (idx: number) => {
    const updated = { ...approvedOrders };
    updated.orders[idx].checkedValue = !updated.orders[idx].checkedValue;
    setApprovedOrders(updated);
    let allChecked = true;
    for (const o of updated.orders) {
      if (!o.checkedValue) { allChecked = false; break; }
    }
    setFlagCheckHeader(allChecked);
    enableSignButton(updated.orders);
  };

  const openSignPopUp = () => {
    setShowSignOrdersPopUp(true);
    if (passwordInputRef.current) {
      passwordInputRef.current.value = "";
    }
    setTimeout(() => {
      if (passwordInputRef.current) passwordInputRef.current.focus();
    }, 0);
  };

  const signCsosOrders = (axwayPassword: string) => {
    const updateOrderRequest = new OrdersUpdate();
    updateOrderRequest.axwayPassword = axwayPassword;
    updateOrderRequest.operationName = CommonConstants.SIGNED_ORDER_OPERATION;
    updateOrderRequest.statusCode = Constants.APPROVED;
    let totalSent = 0;
    const orders: Ordersnew[] = [];
    for (const o of approvedOrders.orders) {
      if (o.checkedValue) {
        const orderDetail = new Ordersnew();
        orderDetail.orderId = o.orderId;
        if (o.account && o.account.indexOf("-") !== -1) {
          const accounts = o.account.split("-");
          orderDetail.distributionCenter = +accounts[0];
          orderDetail.customerNumber = +accounts[1];
        }
        orders.push(orderDetail);
        totalSent++;
      }
    }
    updateOrderRequest.orders = orders;
    setTotalOrderSentForSigning(totalSent);

    csosApiService.updateCsosOrders(updateOrderRequest).then((response) => {
      if (response.body) {
        const updateResponse = response.body;
        if (updateResponse.hostMessage === "SUCCESS") {
          if (updateResponse.unSignedOrders && updateResponse.unSignedOrders.length > 0) {
            setActualOrderSentForSigning(totalSent - updateResponse.unSignedOrders.length);
            setShowOverRiddenPopUp(true);
          } else {
            setShowSignOrdersPopUp(false);
            setShowSignApproveOrderModal(false);
            onSignEmit();
          }
        } else {
          setFailureMessage(CommonConstants.SIGNED_NOT_SIGNED);
          setShowFailedMessagePopUp(true);
        }
      }
    }).catch(() => {
      setFailureMessage(CommonConstants.SIGNED_NOT_SIGNED);
      setShowFailedMessagePopUp(true);
    });
  };

  const closeOverRiddenLayer = () => {
    setShowOverRiddenPopUp(false);
    setShowSignOrdersPopUp(false);
    setShowSignApproveOrderModal(false);
    onSignEmit();
  };

  const closeFailedLayer = () => {
    setShowFailedMessagePopUp(false);
    if (passwordInputRef.current) {
      passwordInputRef.current.value = "";
      passwordInputRef.current.focus();
    }
  };

  return (
    <>
      {/* Sign Approve Order Modal */}
      <ModalContentCenter visible={showSignApproveOrderModal}>
        <div className="modal-fade signApprove-modal" role="dialog">
          <div className="modal-dialog modal-lg">
            <div className="modal-content modal-layout">
              <div className="signApproveOrder-header">
                <button type="button" className="close close-button" onClick={() => setShowSignApproveOrderModal(false)}>&times;</button>
                <h4 className="sign-modal-title">Order Review &amp; Sign</h4>
              </div>
              <div className="sign-modal-body">
                <div>
                  <span>
                    <label>{approvedOrders.totalNumOfApprovedOrders} Approved CSOS orders to be signed by {loggedInUser}:</label>
                  </span>
                </div>
                <div className="table-container signApproveOrder-table">
                  <table className="table table-bordered">
                    <thead className="signApproveOrder-table-header">
                      <tr className="row-header">
                        <th className="text-align-center">
                          SEL<br />
                          <input type="checkbox" checked={flagCheckHeader} onChange={selectHeader} />
                        </th>
                        <th className="text-align-center">ACCOUNT</th>
                        <th className="text-align-center">PO # / ORDER #</th>
                        <th className="text-align-center">UTN</th>
                        <th className="text-align-center"># OF LINES</th>
                        <th className="text-align-center">TOTAL COST</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvedOrders.orders.map((order, idx) => (
                        <tr key={idx} className="row-data">
                          <td className="text-align-center">
                            <input type="checkbox" checked={order.checkedValue || false} onChange={() => updateSelectAll(idx)} />
                          </td>
                          <td className="text-align-left">{order.customerName}<br />{order.account}</td>
                          <td className="text-align-center">{order.purchaseOrderId !== "" ? order.purchaseOrderId : order.orderId}</td>
                          <td className="text-align-center">{order.uniqueTrackingNum}</td>
                          <td className="text-align-center">{order.noOfLines}</td>
                          <td className="text-align-right">{formatCurrency(order.totalCost)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="modal-footer">
                  <input type="button" value="Cancel" className="btn btn-default cancelbtn nonprimary" onClick={() => setShowSignApproveOrderModal(false)} />
                  <input type="button" disabled={!enableButton} value="Sign Orders" className="btn btn-default signorder" onClick={openSignPopUp} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      {/* Sign Password Pop Up */}
      <ModalContentCenter visible={showSignOrdersPopUp}>
        <div className="modal-fade signOrders-modal" role="dialog">
          <div className="modal-dialog">
            <div className="modal-content modal-layout-sign-pop-up">
              <div className="sign-modal-header">
                <button type="button" className="close" onClick={() => setShowSignOrdersPopUp(false)}>&times;</button>
                <h4 className="sign-modal-title">Sign Your Order</h4>
              </div>
              <div className="sign-modal-body">
                <div>To sign your order, enter the password you created when registering your DEA Certificate.</div>
                <div className="form-group sign-box">
                  <label className="signing-text" htmlFor="signBulkOrderPwd">Signing Password:</label>
                  <div className="sign-password">
                    <input
                      type="password"
                      className="form-control"
                      ref={passwordInputRef}
                      id="signBulkOrderPwd"
                      onKeyUp={(e) => { if (e.key === "Enter") signCsosOrders((e.target as HTMLInputElement).value); }}
                    />
                  </div>
                </div>
                <p>If necessary, click Forgot Password below to register your certificate again and create a new password. Please contact the Customer Service Center at 800-326-6457 if you require additional support.</p>
              </div>
              <div className="modal-footer">
                  <a className="forgotpasswd" target="_blank" rel="noopener noreferrer" href={Constants.REGISTER_CERTIFICATE_URL}>Forgot Password</a>
                <button type="button" className="btn btn-default cancelbtn nonprimary" onClick={() => setShowSignOrdersPopUp(false)}>Cancel</button>
                <button type="button" className="btn btn-default signorder" onClick={() => signCsosOrders(passwordInputRef.current?.value || "")}>Sign Order</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      {/* Override Pop Up */}
      <ModalContentCenter visible={showOverRiddenPopUp}>
        <div className="modal-fade overRidden-modal" role="dialog">
          <div className="modal-dialog">
            <div className="modal-content modal-layout-overridden">
              <div className="sign-modal-header">
                <h4 className="sign-modal-title">Alert</h4>
              </div>
              <div className="sign-modal-body">
                <p>{actualOrderSentForSigning} of {totalOrderSentForSigning} Approved Orders were sent for signing. The remaining orders were overridden by another approver.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-default signorder" onClick={closeOverRiddenLayer}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>

      {/* Failed Message Pop Up */}
      <ModalContentCenter visible={showFailedMessagePopUp}>
        <div className="modal-fade failedMessage-modal" role="dialog">
          <div className="modal-dialog">
            <div className="modal-content modal-layout-axway-failed">
              <div className="sign-modal-header">
                <h4 className="sign-modal-title">Alert</h4>
              </div>
              <div className="sign-modal-body">
                <p>{failureMessage}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-default signorder" onClick={closeFailedLayer}>OK</button>
              </div>
            </div>
          </div>
        </div>
      </ModalContentCenter>
    </>
  );
};

export default SignApproveOrderLayer;
